import React, { useState, useRef } from 'react';
import { Helmet } from 'react-helmet-async';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent } from '../components/ui/card';
import { useToast } from '../hooks/use-toast';
import { Printer, Download, RefreshCw, Send, Upload, X, Eye } from 'lucide-react';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';

const SenderReceiverFormPage = () => {
  const { toast } = useToast();
  const printRef = useRef(null);
  const senderIdInputRef = useRef(null);
  const receiverIdInputRef = useRef(null);

  const [sender, setSender] = useState({
    fullName: '',
    phone: '',
    email: '',
    address: '',
    unit: '',
    city: '',
    province: '',
    country: '',
    postalCode: ''
  });

  const [receiver, setReceiver] = useState({
    fullName: '',
    phone: '',
    email: '',
    address: '',
    unit: '',
    city: '',
    province: '',
    country: '',
    postalCode: ''
  });

  const [senderIdFiles, setSenderIdFiles] = useState([]);
  const [receiverIdFiles, setReceiverIdFiles] = useState([]);

  const validateFile = (file) => {
    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'application/pdf'];
    const maxSize = 5 * 1024 * 1024; // 5MB

    if (!allowedTypes.includes(file.type)) {
      toast({
        title: "Invalid File Type",
        description: "Only JPG, JPEG, PNG, and PDF files are allowed.",
        variant: "destructive"
      });
      return false;
    }

    if (file.size > maxSize) {
      toast({
        title: "File Too Large",
        description: "File size must be less than 5MB.",
        variant: "destructive"
      });
      return false;
    }

    return true;
  };

  const handleSenderIdUpload = (e) => {
    const files = Array.from(e.target.files);
    const validFiles = files.filter(validateFile);

    if (validFiles.length > 0) {
      setSenderIdFiles([...senderIdFiles, ...validFiles].slice(0, 2)); // Max 2 files (front/back)
      toast({
        title: "Sender ID Uploaded",
        description: `${validFiles.length} file(s) uploaded successfully.`
      });
    }
  };

  const handleReceiverIdUpload = (e) => {
    const files = Array.from(e.target.files);
    const validFiles = files.filter(validateFile);

    if (validFiles.length > 0) {
      setReceiverIdFiles([...receiverIdFiles, ...validFiles].slice(0, 2)); // Max 2 files (front/back)
      toast({
        title: "Receiver ID Uploaded",
        description: `${validFiles.length} file(s) uploaded successfully.`
      });
    }
  };

  const removeSenderIdFile = (index) => {
    setSenderIdFiles(senderIdFiles.filter((_, i) => i !== index));
  };

  const removeReceiverIdFile = (index) => {
    setReceiverIdFiles(receiverIdFiles.filter((_, i) => i !== index));
  };

  const clearForm = () => {
    if (!window.confirm('Are you sure you want to clear all form data?')) return;

    setSender({
      fullName: '',
      phone: '',
      email: '',
      address: '',
      unit: '',
      city: '',
      province: '',
      country: '',
      postalCode: ''
    });
    setReceiver({
      fullName: '',
      phone: '',
      email: '',
      address: '',
      unit: '',
      city: '',
      province: '',
      country: '',
      postalCode: ''
    });
    setSenderIdFiles([]);
    setReceiverIdFiles([]);

    toast({
      title: "Form Cleared",
      description: "All fields have been reset."
    });
  };

  const validateForm = () => {
    // All fields are now optional - no validation required
    return true;
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;

    try {
      const formData = new FormData();
      
      // Add sender data
      formData.append('sender_fullName', sender.fullName);
      formData.append('sender_phone', sender.phone);
      formData.append('sender_email', sender.email);
      formData.append('sender_address', sender.address);
      formData.append('sender_unit', sender.unit || '');
      formData.append('sender_city', sender.city);
      formData.append('sender_province', sender.province);
      formData.append('sender_country', sender.country);
      formData.append('sender_postalCode', sender.postalCode);
      
      // Add receiver data
      formData.append('receiver_fullName', receiver.fullName);
      formData.append('receiver_phone', receiver.phone);
      formData.append('receiver_email', receiver.email);
      formData.append('receiver_address', receiver.address);
      formData.append('receiver_unit', receiver.unit || '');
      formData.append('receiver_city', receiver.city);
      formData.append('receiver_province', receiver.province);
      formData.append('receiver_country', receiver.country);
      formData.append('receiver_postalCode', receiver.postalCode);
      
      // Add sender ID files
      if (senderIdFiles[0]) formData.append('senderIdFile0', senderIdFiles[0]);
      if (senderIdFiles[1]) formData.append('senderIdFile1', senderIdFiles[1]);
      
      // Add receiver ID files
      if (receiverIdFiles[0]) formData.append('receiverIdFile0', receiverIdFiles[0]);
      if (receiverIdFiles[1]) formData.append('receiverIdFile1', receiverIdFiles[1]);

      const apiUrl = process.env.REACT_APP_BACKEND_URL;
      if (!apiUrl) {
        toast({
          title: "Configuration Error",
          description: "Backend API is not configured. Please contact support.",
          variant: "destructive"
        });
        return;
      }
      
      const response = await fetch(`${apiUrl}/api/sender-receiver-form`, {
        method: 'POST',
        body: formData
      });

      const result = await response.json();

      if (response.ok) {
        toast({
          title: "Form Submitted Successfully!",
          description: `Reference: ${result.reference} - Your form and ID documents have been securely submitted to Shippzy.`
        });
      } else {
        throw new Error(result.detail || 'Submission failed');
      }
    } catch (error) {
      toast({
        title: "Submission Failed",
        description: error.message || "We couldn't submit your form. Please try again or contact Shippzy.",
        variant: "destructive"
      });
    }
  };

  const handlePrint = () => {
    // Allow printing even if form is empty
    window.print();
  };

  const handleDownloadPDF = async () => {
    // Allow PDF download even if form is empty
    const element = printRef.current;
    const canvas = await html2canvas(element, {
      scale: 2,
      useCORS: true,
      logging: false
    });

    const imgData = canvas.toDataURL('image/png');
    const pdf = new jsPDF('p', 'mm', 'a4');
    const pdfWidth = pdf.internal.pageSize.getWidth();
    const pdfHeight = pdf.internal.pageSize.getHeight();
    
    pdf.addImage(imgData, 'PNG', 0, 0, pdfWidth, pdfHeight);
    pdf.save(`Sender-Receiver-Form-${Date.now()}.pdf`);

    toast({
      title: "PDF Downloaded",
      description: "Your address sheet has been saved."
    });
  };

  return (
    <>
      <Helmet>
        <title>Shippzy Sender–Receiver Form | Shipping Address Sheet</title>
        <meta name="description" content="Complete sender and receiver details online and print a professional shipping address sheet for your Shippzy shipment." />
        <link rel="canonical" href="https://shippzy.ca/sender-receiver-form" />
      </Helmet>

      <style>{`
        @media print {
          body * {
            visibility: hidden;
          }
          #printable-address-sheet, #printable-address-sheet * {
            visibility: visible;
          }
          #printable-address-sheet {
            position: absolute;
            left: 0;
            top: 0;
            width: 100%;
          }
          @page {
            size: A4 portrait;
            margin: 10mm;
          }
          .no-print {
            display: none !important;
          }
          .print-only {
            display: block !important;
          }
          button, nav, footer, .action-buttons, .upload-section, .id-notice {
            display: none !important;
          }
        }
        .print-only {
          display: none;
        }
      `}</style>

      {/* Web View */}
      <div className="min-h-screen bg-gray-50 py-8 px-4 no-print">
        <div className="max-w-5xl mx-auto">
          <Card className="shadow-xl">
            <CardContent className="p-8">
              <div className="text-center mb-8">
                <h1 className="text-3xl font-bold text-gray-900 mb-2">
                  Shippzy Sender–Receiver Form
                </h1>
                <p className="text-gray-600">
                  Enter sender and receiver details online and print a professional shipping address sheet.
                </p>
              </div>

              {/* Sender Details */}
              <div className="mb-8">
                <h2 className="text-xl font-bold text-navy-900 mb-4 border-b-2 border-blue-600 pb-2">
                  SECTION A — SENDER / FROM
                </h2>
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Full Name</label>
                    <Input
                      value={sender.fullName}
                      onChange={(e) => setSender({...sender, fullName: e.target.value})}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Phone Number</label>
                    <Input
                      value={sender.phone}
                      onChange={(e) => setSender({...sender, phone: e.target.value})}
                    />
                  </div>
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-1">Email Address</label>
                    <Input
                      type="email"
                      value={sender.email}
                      onChange={(e) => setSender({...sender, email: e.target.value})}
                    />
                  </div>
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-1">Full Street Address</label>
                    <Input
                      value={sender.address}
                      onChange={(e) => setSender({...sender, address: e.target.value})}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Apartment / Unit Number</label>
                    <Input
                      value={sender.unit}
                      onChange={(e) => setSender({...sender, unit: e.target.value})}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">City</label>
                    <Input
                      value={sender.city}
                      onChange={(e) => setSender({...sender, city: e.target.value})}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Province / State</label>
                    <Input
                      value={sender.province}
                      onChange={(e) => setSender({...sender, province: e.target.value})}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Country</label>
                    <Input
                      value={sender.country}
                      onChange={(e) => setSender({...sender, country: e.target.value})}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Postal / ZIP Code</label>
                    <Input
                      value={sender.postalCode}
                      onChange={(e) => setSender({...sender, postalCode: e.target.value})}
                    />
                  </div>
                </div>
              </div>

              {/* Receiver Details */}
              <div className="mb-8">
                <h2 className="text-xl font-bold text-navy-900 mb-4 border-b-2 border-green-600 pb-2">
                  SECTION B — RECEIVER / TO
                </h2>
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Full Name</label>
                    <Input
                      value={receiver.fullName}
                      onChange={(e) => setReceiver({...receiver, fullName: e.target.value})}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Phone Number</label>
                    <Input
                      value={receiver.phone}
                      onChange={(e) => setReceiver({...receiver, phone: e.target.value})}
                    />
                  </div>
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-1">Email Address</label>
                    <Input
                      type="email"
                      value={receiver.email}
                      onChange={(e) => setReceiver({...receiver, email: e.target.value})}
                    />
                  </div>
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-1">Full Street Address</label>
                    <Input
                      value={receiver.address}
                      onChange={(e) => setReceiver({...receiver, address: e.target.value})}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Apartment / Unit Number</label>
                    <Input
                      value={receiver.unit}
                      onChange={(e) => setReceiver({...receiver, unit: e.target.value})}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">City</label>
                    <Input
                      value={receiver.city}
                      onChange={(e) => setReceiver({...receiver, city: e.target.value})}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Province / State</label>
                    <Input
                      value={receiver.province}
                      onChange={(e) => setReceiver({...receiver, province: e.target.value})}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Country</label>
                    <Input
                      value={receiver.country}
                      onChange={(e) => setReceiver({...receiver, country: e.target.value})}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Postal / ZIP Code</label>
                    <Input
                      value={receiver.postalCode}
                      onChange={(e) => setReceiver({...receiver, postalCode: e.target.value})}
                    />
                  </div>
                </div>
              </div>

              {/* ID Uploads */}
              <div className="mb-8 upload-section">
                <h2 className="text-xl font-bold text-navy-900 mb-4 border-b-2 border-red-600 pb-2">
                  SECTION C — IDENTIFICATION DOCUMENTS
                </h2>
                
                <div className="bg-blue-50 border-l-4 border-blue-600 p-4 mb-6 id-notice">
                  <p className="text-sm text-blue-900">
                    <strong>Privacy Notice:</strong> Identification documents are collected for shipment verification and customs compliance. 
                    Files are securely stored and accessible only to authorized Shippzy staff. ID information will NOT appear on printed address sheets.
                  </p>
                </div>

                <div className="grid md:grid-cols-2 gap-6">
                  {/* Sender ID Upload */}
                  <div className="border-2 border-dashed border-gray-300 rounded-lg p-6">
                    <h3 className="font-bold text-gray-900 mb-4">Upload Sender ID</h3>
                    <input
                      type="file"
                      ref={senderIdInputRef}
                      onChange={handleSenderIdUpload}
                      accept=".jpg,.jpeg,.png,.pdf"
                      multiple
                      className="hidden"
                    />
                    <Button
                      onClick={() => senderIdInputRef.current?.click()}
                      variant="outline"
                      className="w-full mb-4"
                      disabled={senderIdFiles.length >= 2}
                    >
                      <Upload className="h-4 w-4 mr-2" />
                      Choose Sender ID Files
                    </Button>
                    <p className="text-xs text-gray-600 mb-4">
                      Accepted: JPG, JPEG, PNG, PDF (max 5MB each, up to 2 files)
                    </p>
                    {senderIdFiles.length > 0 && (
                      <div className="space-y-2">
                        {senderIdFiles.map((file, index) => (
                          <div key={index} className="flex items-center justify-between bg-gray-50 p-2 rounded">
                            <span className="text-sm truncate flex-1">{file.name}</span>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => removeSenderIdFile(index)}
                            >
                              <X className="h-4 w-4 text-red-600" />
                            </Button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  {/* Receiver ID Upload */}
                  <div className="border-2 border-dashed border-gray-300 rounded-lg p-6">
                    <h3 className="font-bold text-gray-900 mb-4">Upload Receiver ID</h3>
                    <input
                      type="file"
                      ref={receiverIdInputRef}
                      onChange={handleReceiverIdUpload}
                      accept=".jpg,.jpeg,.png,.pdf"
                      multiple
                      className="hidden"
                    />
                    <Button
                      onClick={() => receiverIdInputRef.current?.click()}
                      variant="outline"
                      className="w-full mb-4"
                      disabled={receiverIdFiles.length >= 2}
                    >
                      <Upload className="h-4 w-4 mr-2" />
                      Choose Receiver ID Files
                    </Button>
                    <p className="text-xs text-gray-600 mb-4">
                      Accepted: JPG, JPEG, PNG, PDF (max 5MB each, up to 2 files)
                    </p>
                    {receiverIdFiles.length > 0 && (
                      <div className="space-y-2">
                        {receiverIdFiles.map((file, index) => (
                          <div key={index} className="flex items-center justify-between bg-gray-50 p-2 rounded">
                            <span className="text-sm truncate flex-1">{file.name}</span>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => removeReceiverIdFile(index)}
                            >
                              <X className="h-4 w-4 text-red-600" />
                            </Button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex flex-wrap gap-4 action-buttons">
                <Button onClick={handleSubmit} className="bg-gradient-to-r from-red-600 to-orange-500 hover:from-red-700 hover:to-orange-600">
                  <Send className="h-4 w-4 mr-2" />
                  Submit Form
                </Button>
                <Button onClick={handlePrint} variant="outline">
                  <Printer className="h-4 w-4 mr-2" />
                  Print Address Sheet
                </Button>
                <Button onClick={handleDownloadPDF} variant="outline">
                  <Download className="h-4 w-4 mr-2" />
                  Download PDF
                </Button>
                <Button onClick={clearForm} variant="outline">
                  <RefreshCw className="h-4 w-4 mr-2" />
                  Clear Form
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Printable Address Sheet */}
      <div id="printable-address-sheet" ref={printRef} className="print-only" style={{ padding: '10mm', backgroundColor: 'white', fontSize: '11pt' }}>
        <div style={{ textAlign: 'center', marginBottom: '20px', borderBottom: '3px solid #DC2626', paddingBottom: '10px' }}>
          <h1 style={{ fontSize: '20pt', fontWeight: 'bold', color: '#1e3a8a', margin: '0 0 5px 0' }}>SHIPPZY INC.</h1>
          <h2 style={{ fontSize: '15pt', fontWeight: 'bold', margin: 0 }}>SHIPPING ADDRESS DETAILS</h2>
        </div>

        {/* FROM Section */}
        <div style={{ border: '3px solid #2563eb', borderRadius: '8px', padding: '20px', marginBottom: '20px', backgroundColor: '#eff6ff' }}>
          <h2 style={{ fontSize: '18pt', fontWeight: 'bold', color: '#1e3a8a', marginTop: 0, marginBottom: '15px', borderBottom: '2px solid #2563eb', paddingBottom: '8px' }}>
            FROM
          </h2>
          <div style={{ fontSize: '11pt', lineHeight: '1.8' }}>
            <p style={{ margin: '8px 0' }}><strong>Name:</strong> {sender.fullName}</p>
            <p style={{ margin: '8px 0' }}><strong>Phone:</strong> {sender.phone}</p>
            <p style={{ margin: '8px 0' }}><strong>Email:</strong> {sender.email}</p>
            <p style={{ margin: '8px 0' }}><strong>Address:</strong> {sender.address}</p>
            {sender.unit && <p style={{ margin: '8px 0' }}><strong>Unit:</strong> {sender.unit}</p>}
            <p style={{ margin: '8px 0' }}>
              <strong>City/Province:</strong> {sender.city}, {sender.province}
            </p>
            <p style={{ margin: '8px 0' }}>
              <strong>Postal Code:</strong> {sender.postalCode}
            </p>
            <p style={{ margin: '8px 0' }}><strong>Country:</strong> {sender.country}</p>
          </div>
        </div>

        {/* TO Section */}
        <div style={{ border: '3px solid #16a34a', borderRadius: '8px', padding: '20px', backgroundColor: '#f0fdf4' }}>
          <h2 style={{ fontSize: '18pt', fontWeight: 'bold', color: '#166534', marginTop: 0, marginBottom: '15px', borderBottom: '2px solid #16a34a', paddingBottom: '8px' }}>
            TO
          </h2>
          <div style={{ fontSize: '11pt', lineHeight: '1.8' }}>
            <p style={{ margin: '8px 0' }}><strong>Name:</strong> {receiver.fullName}</p>
            <p style={{ margin: '8px 0' }}><strong>Phone:</strong> {receiver.phone}</p>
            <p style={{ margin: '8px 0' }}><strong>Email:</strong> {receiver.email}</p>
            <p style={{ margin: '8px 0' }}><strong>Address:</strong> {receiver.address}</p>
            {receiver.unit && <p style={{ margin: '8px 0' }}><strong>Unit:</strong> {receiver.unit}</p>}
            <p style={{ margin: '8px 0' }}>
              <strong>City/Province:</strong> {receiver.city}, {receiver.province}
            </p>
            <p style={{ margin: '8px 0' }}>
              <strong>Postal Code:</strong> {receiver.postalCode}
            </p>
            <p style={{ margin: '8px 0' }}><strong>Country:</strong> {receiver.country}</p>
          </div>
        </div>

        <div style={{ marginTop: '20px', textAlign: 'center', fontSize: '9pt', color: '#666' }}>
          <p>Shippzy Inc. | 140 Advance Blvd, Unit 7, Brampton, ON L6T 4Z8 | +1 (437) 616-0758</p>
        </div>
      </div>
    </>
  );
};

export default SenderReceiverFormPage;
